<script lang="ts">
  import type { ButtonIconPosition, ButtonMode, ButtonSize, ButtonVariant } from './buttonTypes';

  interface Props {
    variant?: ButtonVariant;
    size?: ButtonSize;
    mode?: ButtonMode;
    iconPosition?: ButtonIconPosition;
    rounded?: boolean;
    disabled?: boolean;
    class?: string;
    onclick?: (e: MouseEvent) => void;
    children?: import('svelte').Snippet<[]>;
    [key: string]: unknown;
  }

  let {
    variant = 'primary',
    size = 'md',
    mode = 'both',
    iconPosition = 'leading',
    rounded = false,
    disabled = false,
    class: className = '',
    onclick,
    children,
    ...restProps
  }: Props = $props();

  /** Re-read `restProps` when parent updates; merge stray `class` into the root class, omit from spread. */
  const restSpread = $derived.by(() => {
    const { class: extraFromRest, ...rest } = restProps as Record<string, unknown>;
    return {
      rest,
      extraClass: typeof extraFromRest === 'string' ? extraFromRest : ''
    };
  });
</script>

<button
  class="button {variant} {size} {mode} {className || ''} {restSpread.extraClass}"
  class:rounded
  class:icon-trailing={mode === 'both' && iconPosition === 'trailing'}
  disabled={disabled}
  class:disabled={disabled}
  {onclick}
  {...restSpread.rest}
>
  {@render children?.()}
</button>

<style>
  /* Button styles */
  .button {
    /* Layout */
    display: inline-flex;
    align-items: center;
    justify-content: center;

    /* Box model */
    border: none;
    box-sizing: border-box;

    /* Typography */
    font-family: inherit;
    font-weight: 500;
    line-height: 1;

    /* Other */
    cursor: default;
    outline: none;

    transition:
      background-color var(--motion-effects-fast-duration) var(--motion-effects-fast-easing),
      color var(--motion-effects-fast-duration) var(--motion-effects-fast-easing),
      border-color var(--motion-effects-fast-duration) var(--motion-effects-fast-easing),
      box-shadow var(--motion-effects-fast-duration) var(--motion-effects-fast-easing),
      transform var(--motion-effects-fast-duration) var(--motion-effects-fast-easing),
      opacity var(--motion-effects-fast-duration) var(--motion-effects-fast-easing),
      padding var(--motion-effects-fast-duration) var(--motion-effects-fast-easing),
      gap var(--motion-effects-fast-duration) var(--motion-effects-fast-easing),
      border-radius var(--motion-effects-fast-duration) var(--motion-effects-fast-easing);

    &:active:not(:disabled):not(.disabled) {
      transform: translateY(0.5px);
    }

    &.primary {
      background: var(--primary-bg);
      color: var(--primary-print);
      border: 1px solid var(--primary-border);

      &:hover {
        background: var(--primary-bg-hover);
        color: var(--primary-print-hover);
        border-color: var(--primary-border-hover);
      }

      &:active {
        background: var(--primary-bg-active);
        color: var(--primary-print-active);
        border-color: var(--primary-border-active);
      }

      &:not(.rounded) {
        border-radius: var(--radius-lg);
      }
    }

    &.secondary {
      background: var(--secondary-bg);
      color: var(--secondary-print);

      &:hover {
        background: var(--secondary-bg-hover);
        color: var(--secondary-print-hover);
      }

      &:active {
        background: var(--secondary-bg-active);
        color: var(--secondary-print-active);
      }

      &:not(.rounded) {
        border-radius: var(--radius-lg);
      }
    }

    &.ghost {
      background: var(--ghost-bg);
      color: var(--ghost-print);

      &:hover {
        background: var(--ghost-bg-hover);
        color: var(--ghost-print-hover);
      }

      &:active,
      &.is-active {
        color: var(--ghost-print-active);
        background-color: var(--ghost-bg-active);
        /* Optional theme override: set `--ghost-active-bg-image` to a gradient. */
        background-image: var(--ghost-active-bg-image, none);
      }

      &:not(.rounded) {
        border-radius: var(--radius-lg);
      }
    }

    &.warning {
      background: var(--warning-bg);
      color: var(--warning-print);
      border: 1px solid var(--warning-bg);

      &:hover {
        background: var(--warning-bg-hover);
        color: var(--warning-print-hover);
        border-color: var(--warning-bg-hover);
      }

      &:active {
        background: var(--warning-bg-active);
        color: var(--warning-print-active);
        border-color: var(--warning-bg-active);
      }

      &:not(.rounded) {
        border-radius: var(--radius-lg);
      }
    }

    &.xs {
      min-height: var(--size-xs);
      padding: var(--pd-2xs) var(--pd-sm);
      gap: var(--pd-2xs);
      font-size: var(--text-xs);

      &:not(.rounded) {
        border-radius: var(--radius-sm);
      }

      &.icon-only {
        width: var(--size-xs);
        height: var(--size-xs);
        min-width: var(--size-xs);
        padding: 0;
      }
    }

    &.sm {
      min-height: var(--size-sm);
      padding: var(--pd-xs) var(--pd-md);
      gap: var(--pd-sm);
      font-size: var(--text-sm);

      &:not(.rounded) {
        border-radius: var(--radius-md);
      }

      &.icon-only {
        width: var(--size-md);
        height: var(--size-sm);
        min-width: var(--size-md);
        padding: 0;
      }
    }

    &.md {
      min-height: var(--size-md);
      padding: var(--pd-sm) var(--pd-lg);
      gap: var(--pd-sm);
      font-size: var(--text-md);

      &:not(.rounded) {
        border-radius: var(--radius-lg);
      }

      &.icon-only {
        width: var(--size-md);
        height: var(--size-md);
        min-width: var(--size-md);
        padding: 0;
      }
    }

    &.lg {
      min-width: var(--size-lg);
      padding: var(--pd-md) var(--pd-xl);
      gap: var(--pd-sm);
      font-size: var(--text-lg);

      &:not(.rounded) {
        border-radius: var(--radius-xl);
      }

      &.icon-only {
        height: var(--size-lg);
        min-width: var(--size-lg);
        padding: 0;
      }
    }

    &.icon-only,
    &.both {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &.rounded {
      border-radius: 50%;
    }

    &:disabled,
    &.disabled {
      opacity: var(--opacity-disabled);
      cursor: not-allowed;
      pointer-events: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .button {
      transition-duration: 1ms;
      transform: none;

      &:active:not(:disabled):not(.disabled) {
        transform: none;
      }
    }
  }
</style>
